import { Edit, Spinner } from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import { Drawer } from 'antd'
import { Tag } from 'antd/lib'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { StockRecord } from '../../types'
import { getAdministrationStatusDisplay } from '../../utils/administrationUtils'
import { AdministrationRecordDetailContent } from './AdministrationRecordDetailContent'

type AdministrationRecordDrawerProps = {
  isVisible: boolean
  onClose: () => void
  record: StockRecord | undefined
  medicationName: string
  controlledDrug?: boolean
  onEdit?: (record: StockRecord) => void
  canEdit?: boolean
  showDoseOutcome?: boolean
}

export const AdministrationRecordDrawer = ({
  isVisible,
  onClose,
  record,
  medicationName,
  controlledDrug,
  onEdit,
  canEdit = true,
  showDoseOutcome = false
}: AdministrationRecordDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isLoading = false

  if (
    !record ||
    record.type !== 'administration' ||
    !record.administrationStatus
  )
    return null

  const statusDisplay = getAdministrationStatusDisplay(
    record?.administrationStatus
  )
  const statusTitle = t(statusDisplay.translationKey)

  const handleEdit = () => {
    if (onEdit && record) {
      onEdit(record)
    }
  }

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && record && (
            <TitleWrapper data-testid="emar-administration-drawer-title">
              <MedicationNameRow data-testid="emar-administration-drawer-medication-name-row">
                <MedicationNameText data-testid="emar-administration-drawer-medication-name">
                  {medicationName}
                </MedicationNameText>
                {controlledDrug && (
                  <Tag
                    color="red"
                    data-testid="emar-administration-drawer-cd-tag"
                  >
                    {t('CD')}
                  </Tag>
                )}
              </MedicationNameRow>
              <TitleContainer data-testid="emar-administration-drawer-status">
                <IconWrapper>{statusDisplay.display}</IconWrapper>
                <StyledTitleHeading level={4}>{statusTitle}</StyledTitleHeading>
              </TitleContainer>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
      data-testid="emar-administration-record-drawer"
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && record && (
        <DrawerContent data-testid="emar-administration-drawer-content">
          <TopContent data-testid="emar-administration-drawer-top-content">
            <DetailsHeading
              level={3}
              data-testid="emar-administration-drawer-details-heading"
            >
              {t('DETAILS')}
            </DetailsHeading>
            {canEdit && (
              <Button
                variant="text"
                onClick={handleEdit}
                data-testid="emar-administration-drawer-edit-button"
              >
                <Edit />
                {t('EDIT')}
              </Button>
            )}
          </TopContent>
          <AdministrationRecordDetailContent
            record={record}
            testId="emar-administration-drawer"
            showDoseOutcome={showDoseOutcome}
          />
        </DrawerContent>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledTitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const DrawerContent = styled.div``

const MedicationNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationNameText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 0 16px 0;
`

const DetailsHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
  margin: 0;
`
